import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { SetAllUsers } from 'app/library/all-users/actions';
import { AllUsersStateModel } from 'app/library/all-users/models/all-users.state-model';
import { FeatureService } from 'app/library/feature/services/feature.service';
import { UserService } from 'app/library/user/services/user.service';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { SubentityState } from 'app/projects/subentity/src/lib/models/subentity.state';
import { RefreshUsersOnlineState } from 'app/projects/user/src/lib/actions/refresh-users-online-state.action';
import { GlobalParticipant } from 'app/projects/user/src/lib/models/participant.model';
import { User } from 'app/projects/user/src/lib/models/user';
import { map } from 'rxjs/operators';
import {
    CreateTeamSuccess,
    FetchSubTeamWithSubteamsProgress,
    FetchSubTeamWithSubteamsProgressFailure,
    FetchSubTeamWithSubteamsProgressSuccess,
    FetchTeamLeaderTeams,
    FetchTeamLeaderTeamsFailure,
    FetchTeamLeaderTeamsSuccess,
    FetchTeamLeaders,
    FetchTeamLeadersFailure,
    FetchTeamLeadersSuccess,
    FetchTeamMembers,
    FetchTeamMembersFailure,
    FetchTeamMembersSuccess,
    FetchTeamMembersUsers,
    FetchTeamMembersUsersFailure,
    FetchTeamMembersUsersSuccess,
    FetchTeamSubTeams,
    FetchTeamSubTeamsFailure,
    FetchTeamSubTeamsSuccess,
    FetchTeamUsers,
    FetchTeamUsersFailure,
    FetchTeamUsersSuccess,
    FetchTeamWatchers,
    FetchTeamWatchersFailure,
    FetchTeamWatchersSuccess,
    FetchTeamsUsers,
    FetchTeamsUsersFailure,
    FetchTeamsUsersSuccess,
    FetchUserOtherTeams,
    FetchUserOtherTeamsFailure,
    FetchUserOtherTeamsSuccess,
    FetchUserProgress,
    FetchUserProgressFailure,
    FetchUserProgressSuccess,
    FetchUserTeams,
    FetchUserTeamsFailure,
    FetchUserTeamsSuccess,
    FetchUserTeamsV1,
    FetchUserTeamsV1Failure,
    FetchUserTeamsV1Success,
    RemoveTeamLeaders,
    RemoveTeamLeadersFailure,
    RemoveTeamMembers,
    RemoveTeamMembersFailure,
    RemoveTeamMembersSuccess,
    RemoveTeamWatchers,
    RemoveTeamWatchersFailure,
    RemoveTeamWatchersSuccess,
    SearchTeams,
    SearchTeamsFailure,
    SearchTeamsSuccess,
    SelectFilteredTeams,
    SetTeams,
} from '../actions';
import { AddTeams } from '../actions/add-entities.action';
import { TeamApiService } from '../services/team.api-service';
import { FUSE_ASYNC_DRAWER } from 'app/projects/fuse/src/lib/services/async-drawer.token';
import { AsyncJobApiService } from 'app/services/async-job.api-service';
import { TeamMember } from './team-member.model';
import { TeamTree } from './team-tree.model';
import { Team } from './team.model';
import { TeamState } from './team.state';
import { TeamsStateModel } from './teams.state-model';
import { UserTeam } from './user-team.model';

@State<TeamsStateModel>({
    name: 'teams',
    defaults: new TeamsStateModel(),
})
@Injectable()
export class TeamsState extends SubentityState<Team> {
    @Selector([TeamState.getEntities])
    static getFilteredEntities(state: TeamsStateModel, teams: Team[]): Team[] {
        if (!teams) {
            return null;
        }

        return teams
            .filter((item) => state.filtered.indexOf(item.id) !== -1)
            .map((item) => {
                return [state.list.indexOf(item.id), item];
            })
            .sort((a: [number, Team], b: [number, Team]) => {
                return a[0] > b[0] ? 1 : b[0] > a[0] ? -1 : 0;
            })
            .map((x: [number, Team]) => x[1]);
    }

    @Selector()
    static getSelectedEntityIds(state: TeamsStateModel): string[] {
        return state.selected;
    }

    @Selector()
    static getSearchTerm(state: TeamsStateModel): string {
        return state.searchTerm;
    }

    @Selector()
    static getTotalCount(state: TeamsStateModel): number {
        return state.totalCount;
    }

    @Selector()
    static isFetchingList(state: TeamsStateModel): boolean {
        return state.fetchingList;
    }

    @Selector()
    static getTeamMembers(state: TeamsStateModel): TeamMember[] {
        return state.teamMembers;
    }

    @Selector()
    static getTeamMembersTotalCount(state: TeamsStateModel): number {
        return state.teamMembersTotalCount;
    }

    @Selector()
    static getTeamMembersLoading(state: TeamsStateModel): boolean {
        return state.teamMembersLoading;
    }

    @Selector()
    static getTeamMembersUsers(state: TeamsStateModel): GlobalParticipant[] {
        return state.teamMembersUsers;
    }

    @Selector()
    static getTeamLeaders(state: TeamsStateModel): User[] {
        return state.teamLeaders;
    }

    @Selector()
    static getTeamWatchers(state: TeamsStateModel): User[] {
        return state.teamWatchers;
    }

    @Selector()
    static getUserTeams(state: TeamsStateModel): UserTeam[] {
        return state.userTeams;
    }

    @Selector()
    static getTeamSubTeams(state: TeamsStateModel): Team[] {
        return state.teamSubTeams;
    }

    @Selector()
    static getTeamSubTeamsTotalCount(state: TeamsStateModel): number {
        return state.teamSubTeamsTotalCount;
    }

    @Selector()
    static getTeamSubTeamsLoading(state: TeamsStateModel): boolean {
        return state.teamSubTeamsLoading;
    }

    @Selector()
    static getTeamLeaderTeams(state: TeamsStateModel): Team[] {
        return state.teamLeaderTeams;
    }

    @Selector()
    static getUserProgress(state: TeamsStateModel): TeamMember {
        return state.userProgress;
    }

    @Selector()
    static getUserOtherTeams(state: TeamsStateModel): Team[] {
        return state.userOtherTeams;
    }

    @Selector()
    static getTeamMemberTeams(state: TeamsStateModel): Team[] {
        return state.teamMemberTeams;
    }

    constructor(
        private _store: Store,
        private _teamApiService: TeamApiService,
        private _featureService: FeatureService,
        private _userService: UserService,
        @Inject(FUSE_ASYNC_DRAWER) private _asyncJobApiService: AsyncJobApiService
    ) {
        super(null, 0);
    }

    @Action(AddTeams)
    addEntities(ctx: StateContext<TeamsStateModel>, action: AddTeams): void {
        const teams = this._store.selectSnapshot(TeamState.getEntities) || [];

        this._patchSubentities(ctx, { payload: { subentityIds: action.payload.map((entity) => entity.id) } }, teams);
    }

    @Action(CreateTeamSuccess)
    createEntity(ctx: StateContext<TeamsStateModel>, action: CreateTeamSuccess): void {
        const teams = this._store.selectSnapshot(TeamState.getEntities) || [];

        this._patchSubentities(ctx, { payload: { subentityIds: [action.payload.id] } }, teams);
    }

    @Action(SetTeams)
    setEntities(ctx: StateContext<TeamsStateModel>, action: SetTeams): void {
        this._setSubentities(ctx, action, null);
    }

    @Action(SelectFilteredTeams)
    selectAll(ctx: StateContext<TeamsStateModel>): void {
        this._selectFilteredSubentities(ctx);
    }

    @Action(SearchTeams)
    searchTeamsTree(ctx: StateContext<TeamsStateModel>, action: SearchTeams): void {
        ctx.patchState({
            pageSize: action.payload.pageSize,
            pageNumber: action.payload.pageNumber,
        });

        this._teamApiService.searchTeamsTree(action.payload.search, action.payload.pageNumber, action.payload.pageSize).subscribe(
            (data) => {
                ctx.patchState({
                    totalCount: data.pagination.number_of_results,
                });

                const teamTree = data.data.map((query) => new TeamTree(query));

                ctx.dispatch(new SearchTeamsSuccess(teamTree));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new SearchTeamsFailure());
            }
        );
    }

    @Action(FetchUserTeams)
    fetchUserTeams({ dispatch, patchState }: StateContext<TeamsStateModel>, action: FetchUserTeams): void {
        this._teamApiService.getUserTeams(action.payload.userId).subscribe(
            (entities) => {
                dispatch([new FetchUserTeamsSuccess(entities)]);

                patchState({
                    userTeams: entities,
                });
            },
            (response: HttpErrorResponse) => {
                dispatch(new FetchUserTeamsFailure({ entityId: action.payload.userId, response }));
            }
        );
    }

    @Action(FetchUserTeamsV1)
    fetchUserTeamsV1({ dispatch, patchState }: StateContext<TeamsStateModel>, action: FetchUserTeamsV1): void {
        this._teamApiService.getUserTeamsV1(action.payload.userId, action.payload.role).subscribe(
            (entities) => {
                dispatch([new FetchUserTeamsV1Success(entities)]);

                patchState({
                    teamMemberTeams: entities,
                });
            },
            (response: HttpErrorResponse) => {
                dispatch(new FetchUserTeamsV1Failure({ entityId: action.payload.userId, response }));
            }
        );
    }

    @Action(FetchTeamsUsers)
    fetchAvailableUsers(ctx: StateContext<AllUsersStateModel>, action: FetchTeamsUsers): void {
        ctx.patchState({
            fetchingList: true,
        });

        this._teamApiService
            .getTeamsUsers(action.payload.teamId, action.payload.search, action.payload.pageSize, action.payload.pageNumber, action.payload.role)
            .pipe(
                map((data) => {
                    ctx.patchState({
                        totalCount: data.pagination.number_of_results,
                        fetchingList: false,
                    });

                    return data.data.map((query) => new User(query));
                })
            )
            .subscribe(
                (entities) => {
                    this._store.dispatch([new FetchTeamsUsersSuccess(entities), new SetAllUsers(entities)]);
                },
                (error: HttpErrorResponse) => {
                    this._store.dispatch(new FetchTeamsUsersFailure());
                }
            );
    }

    @Action(FetchTeamUsers)
    fetchTeamUsers(ctx: StateContext<TeamsStateModel>, action: FetchTeamUsers): void {
        this._teamApiService.getTeamUsers(action.payload.teamId, action.payload.selectedTeamId, action.payload.role).subscribe(
            (entities) => {
                ctx.dispatch(new FetchTeamUsersSuccess(entities));
            },
            (response: HttpErrorResponse) => {
                ctx.dispatch(new FetchTeamUsersFailure({ entityId: action.payload.teamId, response }));
            }
        );
    }
    /* Team Leaders */

    @Action(FetchUserProgress)
    fetchUserProgress({ dispatch, patchState }: StateContext<TeamsStateModel>, payload: FetchUserProgress): void {
        this._teamApiService.getUserProgress(payload.userId).subscribe(
            (entity) => {
                dispatch(new FetchUserProgressSuccess(entity));

                patchState({
                    userProgress: entity,
                });
            },
            (response: HttpErrorResponse) => {
                dispatch(new FetchUserProgressFailure());
            }
        );
    }

    @Action(FetchUserOtherTeams)
    fetchUserOtherTeams({ dispatch, patchState }: StateContext<TeamsStateModel>, payload: FetchUserOtherTeams): void {
        this._teamApiService.getUserOtherTeams(payload.teamId, payload.userId).subscribe(
            (entities) => {
                dispatch(new FetchUserOtherTeamsSuccess(entities));

                patchState({
                    userOtherTeams: entities,
                });
            },
            (response: HttpErrorResponse) => {
                dispatch(new FetchUserOtherTeamsFailure());
            }
        );
    }

    /* Team Members */

    @Action(FetchTeamMembers)
    fetchTeamMembers({ dispatch, patchState, getState }: StateContext<TeamsStateModel>, action: FetchTeamMembers): void {
        const { teamId, page = 1, limit = 6, append = false } = action.payload;

        patchState({ teamMembersLoading: true });

        this._teamApiService.getTeamMembers(teamId, page, limit).subscribe(
            (response) => {
                const state = getState();
                const entities = append ? [...state.teamMembers, ...response.data] : response.data;

                dispatch(new FetchTeamMembersSuccess(entities));

                patchState({
                    teamMembers: entities,
                    teamMembersTotalCount: response.pagination.number_of_results,
                    teamMembersLoading: false,
                });
            },
            (response: HttpErrorResponse) => {
                patchState({ teamMembersLoading: false });
                dispatch(new FetchTeamMembersFailure());
            }
        );
    }

    @Action(FetchTeamMembersUsers)
    fetchTeamMembersUsers({ dispatch, patchState }: StateContext<TeamsStateModel>, { payload }: FetchTeamMembersUsers): void {
        this._teamApiService.getTeamMembersUsers(payload.teamId, payload.includeSubteams).subscribe(
            (entities) => {
                dispatch(new FetchTeamMembersUsersSuccess(entities));

                patchState({
                    teamMembersUsers: entities,
                });
            },
            (response: HttpErrorResponse) => {
                dispatch(new FetchTeamMembersUsersFailure());
            }
        );
    }

    @Action(RemoveTeamMembers)
    removeTeamMembers({ dispatch, patchState, getState }: StateContext<TeamsStateModel>, { payload }: RemoveTeamMembers): void {
        this._teamApiService.removeTeamMembers(payload).subscribe(
            (result) => {
                if (result.asyncJobId) {
                    dispatch(new RemoveTeamMembersSuccess({ asyncJobId: result.asyncJobId }));
                    this._asyncJobApiService.trackEnqueuedJob(result.asyncJobId, () => {
                        dispatch(new FetchTeamMembers({ teamId: payload.teamId }));
                    });
                } else {
                    const state = getState();
                    const filteredTeamMembersList = state.teamMembers.filter((teamMember) => !payload.userIds.includes(teamMember.user.id));
                    const removedCount = state.teamMembers.length - filteredTeamMembersList.length;

                    dispatch(new RemoveTeamMembersSuccess());

                    patchState({
                        teamMembers: filteredTeamMembersList,
                        teamMembersTotalCount: Math.max(0, state.teamMembersTotalCount - removedCount),
                    });
                }
            },
            (response: HttpErrorResponse) => {
                dispatch(new RemoveTeamMembersFailure({ response }));
            }
        );
    }

    /* Team Leaders */

    @Action(FetchTeamLeaders)
    fetchTeamLeaders({ dispatch, patchState }: StateContext<TeamsStateModel>, payload: FetchTeamLeaders): void {
        this._teamApiService.getTeamLeaders(payload.teamId).subscribe(
            (entities) => {
                dispatch(new FetchTeamLeadersSuccess(entities));

                patchState({
                    teamLeaders: entities,
                });
            },
            (response: HttpErrorResponse) => {
                dispatch(new FetchTeamLeadersFailure());
            }
        );
    }

    @Action(FetchTeamLeaderTeams)
    fetchTeamLeaderTeams({ dispatch, patchState }: StateContext<TeamsStateModel>, payload: FetchTeamLeaderTeams): void {
        this._teamApiService.getTeamLeaderTeams().subscribe(
            (entities) => {
                dispatch(new FetchTeamLeaderTeamsSuccess(entities));

                patchState({
                    teamLeaderTeams: entities,
                });
            },
            (response: HttpErrorResponse) => {
                dispatch(new FetchTeamLeaderTeamsFailure());
            }
        );
    }

    @Action(RemoveTeamLeaders)
    removeTeamLeaders({ dispatch, patchState, getState }: StateContext<TeamsStateModel>, { payload }: RemoveTeamLeaders): void {
        this._teamApiService.removeTeamLeaders(payload).subscribe(
            () => {
                const state = getState();
                const filteredTeamLeadersList = state.teamLeaders.filter((teamLeader) => !payload.userIds.includes(teamLeader.id));

                dispatch(new RemoveTeamWatchersSuccess());

                patchState({
                    teamLeaders: filteredTeamLeadersList,
                });
            },
            (response: HttpErrorResponse) => {
                dispatch(new RemoveTeamLeadersFailure({ response }));
            }
        );
    }

    /* Team Watchers */

    @Action(FetchTeamWatchers)
    fetchTeamWatchers({ dispatch, patchState }: StateContext<TeamsStateModel>, payload: FetchTeamWatchers): void {
        this._teamApiService.getTeamWatchers(payload.teamId).subscribe(
            (entities) => {
                dispatch(new FetchTeamWatchersSuccess(entities));

                patchState({
                    teamWatchers: entities,
                });
            },
            (response: HttpErrorResponse) => {
                dispatch(new FetchTeamWatchersFailure());
            }
        );
    }

    @Action(RemoveTeamWatchers)
    removeTeamWatchers({ dispatch, patchState, getState }: StateContext<TeamsStateModel>, { payload }: RemoveTeamWatchers): void {
        this._teamApiService.removeTeamWatchers(payload).subscribe(
            () => {
                const state = getState();
                const filteredTeamWatchersList = state.teamWatchers.filter((teamLeader) => !payload.userIds.includes(teamLeader.id));

                dispatch(new RemoveTeamWatchersSuccess());

                patchState({
                    teamWatchers: filteredTeamWatchersList,
                });
            },
            (response: HttpErrorResponse) => {
                dispatch(new RemoveTeamWatchersFailure({ response }));
            }
        );
    }

    /* Team SubTeams */
    @Action(FetchTeamSubTeams)
    fetchTeamSubTeams({ dispatch, patchState, getState }: StateContext<TeamsStateModel>, action: FetchTeamSubTeams): void {
        const { teamId, page = 1, limit = 5, query = '', append = false } = action.payload;

        patchState({ teamSubTeamsLoading: true });

        this._teamApiService.getTeamSubTeams(teamId, page, limit, query).subscribe(
            (response) => {
                const state = getState();
                const teams = append ? [...state.teamSubTeams, ...response.data] : response.data;

                dispatch(new FetchTeamSubTeamsSuccess(teams));

                patchState({
                    teamSubTeams: teams,
                    teamSubTeamsTotalCount: response.pagination.number_of_results,
                    teamSubTeamsLoading: false,
                });

                response.data.forEach((team) => {
                    setTimeout(() => {
                        const leaders = team.leaders;
                        if (leaders && this._featureService.checkFeatureStatus(FeatureSwitchName.UserOnlineState)) {
                            this._store.dispatch(new RefreshUsersOnlineState(leaders));
                            this._userService.setOnlineStateInterval(RefreshUsersOnlineState, leaders);
                        }
                    });
                });
            },
            (response: HttpErrorResponse) => {
                patchState({ teamSubTeamsLoading: false });
                dispatch(new FetchTeamSubTeamsFailure({ entityId: teamId, response }));
            }
        );
    }

    @Action(FetchSubTeamWithSubteamsProgress)
    fetchSubTeamWithSubteamsProgress({ dispatch, patchState, getState }: StateContext<TeamsStateModel>, action: FetchSubTeamWithSubteamsProgress): void {
        this._teamApiService.getSubTeamWithSubTeamsProgress(action.payload.teamId, action.payload.includeSubTeamsProgress).subscribe(
            (entity) => {
                dispatch(new FetchSubTeamWithSubteamsProgressSuccess(entity));

                const state = getState();
                const updatedList = state.teamSubTeams.map((item) => (item.id === entity.id ? { ...item, ...entity } : item));

                patchState({
                    teamSubTeams: updatedList,
                });
            },
            (response: HttpErrorResponse) => {
                dispatch(new FetchSubTeamWithSubteamsProgressFailure({ entityId: action.payload.teamId, response }));
            }
        );
    }
}
